import { Truck } from '@numeo/types-core'
import { createSlice, PayloadAction, Slice } from '@reduxjs/toolkit'
import { RootState } from 'store'

/** The seat the not-yet-saved driver takes on the vehicle being added for them; null when they were taken off it. */
export type PendingDriverSeat = 'main' | 'second' | null

export interface PendingTruckAssignment {
    driverName?: string
    driverSeat?: PendingDriverSeat
    /** The owner being created in the form behind the modal. Empty when it has no name typed yet. */
    ownerName?: string
    /** Is the owner being created still this vehicle's owner? Absent means yes; false once another owner, or
     *  none, was picked instead — the opener must then leave the vehicle where the operator put it. */
    ownerChosen?: boolean
}

/**
 * Which Box Truck vehicle form the modal shows. `full` is the whole vehicle editor. `quickAdd` is the short create
 * form a driver or owner form opens: identity, assignments and documents, the rest left for Edit. `driverEquipment`
 * is the whole editor WITHOUT assignments, opened from a driver's profile: that form already decides which vehicle
 * the driver is on, and a second editor of the same link on one screen is how the two end up disagreeing.
 * Reset to `full` when the modal closes. TMS ignores it.
 */
export type TruckModalVariant = 'full' | 'quickAdd' | 'driverEquipment'

/** The form actually shown: Quick Add only creates, so an opener that asked for it while editing gets the whole editor. */
export const effectiveTruckModalVariant = (variant: TruckModalVariant, editing: boolean): TruckModalVariant =>
    variant === 'quickAdd' && editing ? 'full' : variant

interface TrucksState {
    /** Open state for the Terminate confirmation. Replaces the old delete modal — deleting a truck took
     *  its loads' gross out of payroll, P&L and the leaderboard; terminating keeps every one of them. */
    terminateTruckModalOpen: boolean
    /** Open state for the Reactivate confirmation, which can also surface the billing seat prompt. */
    reactivateTruckModalOpen: boolean
    selectedTruck: Truck | null
    assignDriversModalOpen: boolean
    isAddEditTruckModalOpen: boolean
    isTruckEditingMode: boolean
    isTruckSubmitting: boolean
    truckAddEditStep: number
    /** Driver and owner being created in the form behind the modal. Neither has an id yet: each is an option in
     *  its picker, chosen by default — the driver's seat (`driverSeat`) and the owner (`ownerChosen`) are
     *  written once they are saved, unless the operator picked someone else. */
    pendingTruckAssignment: PendingTruckAssignment | null
    /** The id the server gave the truck the modal just created, for the form that opened it to adopt.
     *  Published because the modal saves through the Profiles route's handler, not the form's — and the
     *  alternative, diffing the truck list for an unfamiliar id, picks an arbitrary one as soon as two
     *  arrive together (another tab, another operator in the same tenant). Consumed once, then cleared. */
    lastCreatedTruckId: string | null
    truckModalVariant: TruckModalVariant
}

const initialState: TrucksState = {
    terminateTruckModalOpen: false,
    reactivateTruckModalOpen: false,
    selectedTruck: null,
    assignDriversModalOpen: false,
    isAddEditTruckModalOpen: false,
    isTruckEditingMode: false,
    isTruckSubmitting: false,
    truckAddEditStep: 1,
    pendingTruckAssignment: null,
    lastCreatedTruckId: null,
    truckModalVariant: 'full',
}

const truckSlice: Slice<TrucksState> = createSlice({
    name: 'trucks',
    initialState,
    reducers: {
        setPendingTruckAssignment: (state, action: PayloadAction<TrucksState['pendingTruckAssignment']>) => {
            state.pendingTruckAssignment = action.payload
        },
        setLastCreatedTruckId: (state, action: PayloadAction<string | null>) => {
            state.lastCreatedTruckId = action.payload
        },
        setTruckModalVariant: (state, action: PayloadAction<TruckModalVariant>) => {
            state.truckModalVariant = action.payload
        },
        setTerminateTruckModalOpen(state, action: PayloadAction<boolean>) {
            state.terminateTruckModalOpen = action.payload
        },
        setReactivateTruckModalOpen(state, action: PayloadAction<boolean>) {
            state.reactivateTruckModalOpen = action.payload
        },
        setSelectedTruck(state, action: PayloadAction<Truck | null>) {
            state.selectedTruck = action.payload
        },
        setAssignDriversModalOpen(state, action: PayloadAction<boolean>) {
            state.assignDriversModalOpen = action.payload
        },
        setAddEditTruckModalOpen(state, action: PayloadAction<boolean>) {
            state.isAddEditTruckModalOpen = action.payload
        },
        setTruckEditingMode(state, action: PayloadAction<boolean>) {
            state.isTruckEditingMode = action.payload
        },
        setTruckSubmitting(state, action: PayloadAction<boolean>) {
            state.isTruckSubmitting = action.payload
        },
        setTruckAddEditStep(state, action: PayloadAction<number>) {
            state.truckAddEditStep = action.payload
        },
    },
})

export const {
    setTerminateTruckModalOpen,
    setReactivateTruckModalOpen,
    setSelectedTruck,
    setAssignDriversModalOpen,
    setAddEditTruckModalOpen,
    setTruckEditingMode,
    setTruckSubmitting,
    setTruckAddEditStep,
    setPendingTruckAssignment,
    setLastCreatedTruckId,
    setTruckModalVariant,
} = truckSlice.actions

export const selectTrucksState = (state: RootState) => state.trucks

export default truckSlice.reducer
